import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import websocketService from '../services/websocket';
import TradingViewWidget from '../components/TradingViewWidget';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import {
  TrendingUp,
  LogOut,
  ArrowUp,
  ArrowDown,
  Clock,
  Search,
  Bell,
  Menu,
  ChevronDown,
  X,
  RefreshCw
} from 'lucide-react';

const TradingPage = () => {
  const { symbol } = useParams();
  const { user, logout } = useAuthStore();
  const [selectedPair, setSelectedPair] = useState(symbol || 'BTCUSDT');
  const [orderType, setOrderType] = useState('limit');
  const [side, setSide] = useState('buy');
  const [price, setPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [ticker, setTicker] = useState(null);
  const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
  const [loading, setLoading] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [marketSearch, setMarketSearch] = useState('');
  const [balances, setBalances] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [ordersTab, setOrdersTab] = useState('open');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);

  // Fetch markets
  const fetchMarkets = async () => {
    try {
      const res = await api.get('/markets');
      setMarkets(res.data.tradingPairs || []);
    } catch (e) {
      console.error('Failed to fetch markets');
    }
  };

  // Fetch balances
  const fetchBalances = async () => {
    try {
      const res = await api.get('/wallet/balances');
      setBalances(res.data.balances || []);
    } catch (e) {
      console.error('Failed to fetch balances');
    }
  };

  // Fetch user's orders
  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const [openRes, historyRes] = await Promise.all([
        api.get('/orders/open'),
        api.get('/orders/history?limit=20')
      ]);
      setOpenOrders(openRes.data.orders || []);
      setOrderHistory(historyRes.data.orders || []);
    } catch (e) {
      console.error('Failed to fetch orders:', e);
    }
    setOrdersLoading(false);
  };

  // Cancel order
  const handleCancelOrder = async (orderId) => {
    setCancellingOrder(orderId);
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success('Order cancelled successfully');
      fetchOrders();
      fetchBalances(); // Refresh balances as funds are unlocked
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel order');
    }
    setCancellingOrder(null);
  };

  // Fetch all markets and balances on mount
  useEffect(() => {
    fetchMarkets();
    fetchBalances();
    fetchOrders();
  }, []);

  // Sync selectedPair with URL symbol when navigating
  useEffect(() => {
    if (symbol && symbol !== selectedPair) {
      setSelectedPair(symbol);
      setPrice(''); // Reset price for new pair
      setQuantity(''); // Reset quantity
      setMarketSearch(''); // Clear search
    }
  }, [symbol]);

  useEffect(() => {
    if (selectedPair) {
      fetchMarketData();

      // WebSocket Integration
      const token = localStorage.getItem('token');
      if (token) {
        websocketService.connect(token);
        websocketService.subscribeToMarketData(selectedPair);

        const handleTicker = (data) => {
          if (data.symbol === selectedPair) {
            setTicker(prev => ({
              ...prev,
              lastPrice: data.price,
              priceChangePercent: data.priceChangePercent,
              highPrice: data.high,
              lowPrice: data.low,
              volume: data.volume,
              quoteVolume: data.quoteVolume
            }));
            // Don't auto-update price field - let user control it manually
          }
        };

        const handleOrderBook = (data) => {
          console.log('📋 OrderBook WebSocket data received:', data.symbol, data.bids?.length, data.asks?.length);
          if (data.symbol === selectedPair) {
            setOrderBook(prev => {
              // Simple replacement for now, can be optimized for partial updates
              return {
                bids: data.bids?.slice(0, 15) || [],
                asks: data.asks?.slice(0, 15) || []
              };
            });
          }
        };

        websocketService.on('ticker', handleTicker);
        websocketService.on('orderbook', handleOrderBook);

        return () => {
          websocketService.off('ticker', handleTicker);
          websocketService.off('orderbook', handleOrderBook);
          websocketService.unsubscribeFromMarketData(selectedPair);
        };
      }
    }
  }, [selectedPair]);

  const fetchMarketData = async () => {
    try {
      const [tickerRes, orderBookRes] = await Promise.all([
        api.get(`/markets/${selectedPair}/ticker`),
        api.get(`/markets/${selectedPair}/orderbook`)
      ]);

      const tickerData = tickerRes.data.ticker;
      setTicker({
        ...tickerData,
        lastPrice: tickerData.lastPrice,
        priceChangePercent: tickerData.priceChange24h,
        highPrice: tickerData.high24h,
        lowPrice: tickerData.low24h,
        volume: tickerData.volume24h
      });

      setOrderBook(orderBookRes.data.orderBook);
      if (orderType === 'limit' && !price) {
        setPrice(tickerRes.data.ticker.lastPrice);
      }
    } catch (error) {
      console.error('Failed to load market data', error);
    }
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        tradingPairId: ticker.id,
        type: orderType,
        side,
        quantity: parseFloat(quantity),
        ...(orderType === 'limit' || orderType === 'stop_loss_limit' ? { price: parseFloat(price) } : {}),
        ...(['stop_loss', 'stop_loss_limit'].includes(orderType) ? { stopPrice: parseFloat(stopPrice) } : {})
      };

      await api.post('/orders', orderData);
      toast.success('Order placed successfully!');
      setQuantity('');
      // Refresh data
      fetchMarketData();
      fetchOrders();
      fetchBalances();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-[#0b0e11] text-gray-100 font-sans">
      {/* Navigation */}
      {/* Navigation */}
      <Navbar />

      <div className="max-w-[1600px] mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

          {/* Left Column: Order Book & Recent Trades (Simulated) */}
          <div className="lg:col-span-1 space-y-4">
            {/* Order Book */}
            <div className="bg-[#181a20] rounded-xl border border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-800">
                <h3 className="font-bold text-white text-sm">Order Book</h3>
              </div>
              <div className="p-2">
                <div className="flex justify-between text-xs text-gray-500 mb-2 px-2">
                  <span>Price(USDT)</span>
                  <span>Amount(BTC)</span>
                  <span>Total</span>
                </div>
                {/* Asks (Sells) - Red */}
                <div className="space-y-1 mb-2">
                  {orderBook.asks.slice(0, 10).reverse().map((ask, i) => {
                    const askPrice = Array.isArray(ask) ? ask[0] : ask.price;
                    const askQty = Array.isArray(ask) ? ask[1] : ask.quantity;
                    return (
                      <div key={i} className="flex justify-between text-xs px-2 hover:bg-[#2b3139] cursor-pointer" onClick={() => setPrice(askPrice)}>
                        <span className="text-[#f6465d]">{parseFloat(askPrice).toFixed(8)}</span>
                        <span className="text-gray-300">{parseFloat(askQty).toFixed(5)}</span>
                        <span className="text-gray-500">{(parseFloat(askPrice) * parseFloat(askQty)).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Current Price */}
                <div className="py-2 border-y border-gray-800 px-4 flex items-center gap-2">
                  <span className={`text-xl font-bold ${parseFloat(ticker?.priceChangePercent) >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>
                    {parseFloat(ticker?.lastPrice || 0).toFixed(8)}
                  </span>
                  <span className="text-xs text-gray-400">${parseFloat(ticker?.lastPrice || 0).toFixed(8)}</span>
                </div>

                {/* Bids (Buys) - Green */}
                <div className="space-y-1 mt-2">
                  {orderBook.bids.slice(0, 10).map((bid, i) => {
                    const bidPrice = Array.isArray(bid) ? bid[0] : bid.price;
                    const bidQty = Array.isArray(bid) ? bid[1] : bid.quantity;
                    return (
                      <div key={i} className="flex justify-between text-xs px-2 hover:bg-[#2b3139] cursor-pointer" onClick={() => setPrice(bidPrice)}>
                        <span className="text-[#0ecb81]">{parseFloat(bidPrice).toFixed(8)}</span>
                        <span className="text-gray-300">{parseFloat(bidQty).toFixed(5)}</span>
                        <span className="text-gray-500">{(parseFloat(bidPrice) * parseFloat(bidQty)).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Middle Column: Chart */}
          <div className="lg:col-span-2 space-y-4">
            {/* Ticker Header */}
            <div className="bg-[#181a20] rounded-xl border border-gray-800 p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <button className="flex items-center gap-2 text-2xl font-bold text-white hover:text-gray-300 transition">
                    {selectedPair} <ChevronDown size={20} />
                  </button>
                  {/* Dropdown Placeholder */}
                  <div className="absolute top-full left-0 mt-2 w-72 bg-[#1e2329] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 p-2">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={marketSearch}
                      onChange={(e) => setMarketSearch(e.target.value)}
                      className="w-full bg-[#0b0e11] border border-gray-700 rounded px-2 py-1.5 text-sm text-white mb-2 focus:outline-none focus:border-blue-500"
                    />
                    <div className="max-h-80 overflow-y-auto space-y-0.5">
                      {markets
                        .filter(m => m.symbol.toLowerCase().includes(marketSearch.toLowerCase()))
                        .filter(m => !/((DOWN|UP|BULL|BEAR)USDT|^1000)/.test(m.symbol)) // Exclude leveraged tokens
                        .sort((a, b) => parseFloat(b.volume24h || 0) - parseFloat(a.volume24h || 0))
                        .map(m => (
                          <Link
                            key={m.id}
                            to={`/trade/${m.symbol}`}
                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#2b3139] rounded text-sm text-white"
                          >
                            <img
                              src={`https://assets.coincap.io/assets/icons/${m.baseToken?.symbol?.toLowerCase()}@2x.png`}
                              alt=""
                              className="w-5 h-5 rounded-full"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span>{m.symbol}</span>
                          </Link>
                        ))}
                    </div>
                  </div>
                </div>

                <div className="relative group">
                  <div className="text-xs font-medium px-2 py-1 bg-gray-800 rounded text-[#FCD535] cursor-pointer flex items-center gap-1 hover:bg-gray-700 transition">
                    Spot <ChevronDown size={12} />
                  </div>
                  <div className="absolute top-full left-0 mt-2 w-32 bg-[#1e2329] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
                    <Link to={`/trade/${selectedPair}`} className="block px-4 py-2 text-sm text-[#FCD535] bg-[#2b3139] font-bold">Spot</Link>
                    <Link to={`/futures/${selectedPair}`} className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">Futures</Link>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm">
                <div>
                  <div className="text-gray-500 text-xs">24h Change</div>
                  <div className={`font-medium ${parseFloat(ticker?.priceChangePercent) >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>
                    {parseFloat(ticker?.priceChangePercent || 0).toFixed(2)}%
                  </div>
                </div>
                <div className="hidden sm:block">
                  <div className="text-gray-500 text-xs">24h High</div>
                  <div className="text-white font-medium">{parseFloat(ticker?.highPrice || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs">24h Low</div>
                  <div className="text-white font-medium">{parseFloat(ticker?.lowPrice || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs">24h Volume</div>
                  <div className="text-white font-medium">{parseFloat(ticker?.volume || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Chart Area */}
            <div className="bg-[#181a20] rounded-xl border border-gray-800 h-[300px] sm:h-[500px] relative overflow-hidden">
              <TradingViewWidget symbol={selectedPair} isCustom={ticker?.baseToken?.isCustom} />
            </div>
          </div>

          {/* Right Column: Order Form */}
          <div className="lg:col-span-1">
            <div className="bg-[#181a20] rounded-xl border border-gray-800 p-4">
              <div className="flex gap-2 mb-6 bg-[#0b0e11] p-1 rounded-lg">
                <button
                  onClick={() => setSide('buy')}
                  className={`flex-1 py-2 rounded-md font-bold text-sm transition ${side === 'buy' ? 'bg-[#0ecb81] text-white' : 'text-gray-400 hover:text-white'
                    }`}
                >
                  Buy
                </button>
                <button
                  onClick={() => setSide('sell')}
                  className={`flex-1 py-2 rounded-md font-bold text-sm transition ${side === 'sell' ? 'bg-[#f6465d] text-white' : 'text-gray-400 hover:text-white'
                    }`}
                >
                  Sell
                </button>
              </div>

              <div className="flex justify-between text-xs text-gray-400 mb-4">
                <span>Avail:</span>
                <span className="text-white font-medium">
                  {side === 'buy'
                    ? `${(() => {
                      const w = balances.find(b => b.token?.symbol === 'USDT');
                      return ((parseFloat(w?.balance) || 0) - (parseFloat(w?.lockedBalance) || 0)).toFixed(4);
                    })()} USDT`
                    : `${(() => {
                      const w = balances.find(b => b.token?.symbol === selectedPair.replace('USDT', ''));
                      return ((parseFloat(w?.balance) || 0) - (parseFloat(w?.lockedBalance) || 0)).toFixed(4);
                    })()} ${selectedPair.replace('USDT', '')}`
                  }
                </span>
              </div>

              <form onSubmit={handlePlaceOrder} className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Order Type</label>
                  <select
                    value={orderType}
                    onChange={(e) => setOrderType(e.target.value)}
                    className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="limit">Limit</option>
                    <option value="market">Market</option>
                    <option value="stop_loss">Stop Loss</option>
                    <option value="stop_loss_limit">Stop Limit</option>
                  </select>
                </div>

                {['stop_loss', 'stop_loss_limit'].includes(orderType) && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Stop Price (USDT)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={stopPrice}
                        onChange={(e) => setStopPrice(e.target.value)}
                        className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 text-right"
                        placeholder="0.00"
                      />
                      <span className="absolute left-3 top-2 text-gray-500 text-sm">Stop</span>
                    </div>
                  </div>
                )}

                {['limit', 'stop_loss_limit'].includes(orderType) && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Price (USDT)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 text-right"
                        placeholder="0.00"
                      />
                      <span className="absolute left-3 top-2 text-gray-500 text-sm">Price</span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Amount ({selectedPair.replace('USDT', '')})</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.000001"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 text-right"
                      placeholder="0.00"
                    />
                    <span className="absolute left-3 top-2 text-gray-500 text-sm">Amount</span>
                  </div>
                </div>

                {/* Percentage Slider Placeholder */}
                <div className="flex justify-between gap-1">
                  {[25, 50, 75, 100].map(pct => (
                    <button key={pct} type="button" className="flex-1 bg-[#0b0e11] text-gray-500 text-xs py-1 rounded hover:bg-gray-700 transition">
                      {pct}%
                    </button>
                  ))}
                </div>

                <div className="pt-2">
                  <div className="flex justify-between text-xs text-gray-400 mb-2">
                    <span>Total</span>
                    <span className="text-white">{(parseFloat(price || 0) * parseFloat(quantity || 0)).toFixed(2)} USDT</span>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-3 rounded-lg font-bold text-white transition ${side === 'buy' ? 'bg-[#0ecb81] hover:bg-[#0ecb81]/90' : 'bg-[#f6465d] hover:bg-[#f6465d]/90'
                      }`}
                  >
                    {loading ? 'Processing...' : `${side === 'buy' ? 'Buy' : 'Sell'} ${selectedPair.replace('USDT', '')}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Orders Section */}
        <div className="mt-4 bg-[#181a20] rounded-xl border border-gray-800 overflow-hidden">
          {/* Tabs */}
          <div className="flex items-center justify-between border-b border-gray-800 px-4">
            <div className="flex gap-4">
              <button
                onClick={() => setOrdersTab('open')}
                className={`py-3 text-sm font-medium border-b-2 transition ${ordersTab === 'open'
                  ? 'text-[#FCD535] border-[#FCD535]'
                  : 'text-gray-400 border-transparent hover:text-white'
                  }`}
              >
                Open Orders ({openOrders.length})
              </button>
              <button
                onClick={() => setOrdersTab('history')}
                className={`py-3 text-sm font-medium border-b-2 transition ${ordersTab === 'history'
                  ? 'text-[#FCD535] border-[#FCD535]'
                  : 'text-gray-400 border-transparent hover:text-white'
                  }`}
              >
                Order History
              </button>
            </div>
            <button
              onClick={fetchOrders}
              className="p-2 text-gray-400 hover:text-white transition"
              title="Refresh Orders"
            >
              <RefreshCw size={16} className={ordersLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* Orders Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs border-b border-gray-800">
                  <th className="text-left py-3 px-4 font-medium">Date</th>
                  <th className="text-left py-3 px-4 font-medium">Pair</th>
                  <th className="text-left py-3 px-4 font-medium">Type</th>
                  <th className="text-left py-3 px-4 font-medium">Side</th>
                  <th className="text-right py-3 px-4 font-medium">Price</th>
                  <th className="text-right py-3 px-4 font-medium">Amount</th>
                  <th className="text-right py-3 px-4 font-medium">Filled</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                  {ordersTab === 'open' && (
                    <th className="text-center py-3 px-4 font-medium">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(ordersTab === 'open' ? openOrders : orderHistory).length === 0 ? (
                  <tr>
                    <td colSpan={ordersTab === 'open' ? 9 : 8} className="text-center py-8 text-gray-500">
                      {ordersLoading ? 'Loading orders...' : 'No orders found'}
                    </td>
                  </tr>
                ) : (
                  (ordersTab === 'open' ? openOrders : orderHistory).map((order) => (
                    <tr key={order.id} className="border-b border-gray-800/50 hover:bg-[#1e2329]">
                      <td className="py-3 px-4 text-gray-300 text-xs">
                        {new Date(order.createdAt).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 text-white font-medium">
                        {order.tradingPair?.symbol || '-'}
                      </td>
                      <td className="py-3 px-4 text-gray-300 capitalize">
                        {order.type?.replace('_', ' ')}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${order.side === 'buy' ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>
                          {order.side?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-white">
                        {order.type === 'market'
                          ? (parseFloat(order.averagePrice) > 0
                            ? parseFloat(order.averagePrice).toFixed(4)
                            : 'Market')
                          : parseFloat(order.price || 0).toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-300">
                        {parseFloat(order.quantity || 0).toFixed(6)}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-300">
                        {parseFloat(order.filledQuantity || 0).toFixed(6)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${order.status === 'filled' ? 'bg-green-500/20 text-green-500' :
                          order.status === 'open' ? 'bg-yellow-500/20 text-yellow-500' :
                            order.status === 'cancelled' ? 'bg-gray-500/20 text-gray-500' :
                              order.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                                'bg-blue-500/20 text-blue-500'
                          }`}>
                          {order.status}
                        </span>
                      </td>
                      {ordersTab === 'open' && (
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => handleCancelOrder(order.id)}
                            disabled={cancellingOrder === order.id}
                            className="text-[#f6465d] hover:text-red-400 transition disabled:opacity-50"
                            title="Cancel Order"
                          >
                            {cancellingOrder === order.id ? (
                              <RefreshCw size={16} className="animate-spin" />
                            ) : (
                              <X size={16} />
                            )}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingPage;
